<script lang="ts">
	import {
		DashDeviceWidget,
		LabelV2,
		isOpaqueWhiteActiveBackground,
	} from '@Shelly/shelly-design-system/index';
	import type { ActiveSurfacePresetId, SurfaceGlowConfig } from '@Shelly/shelly-design-system';
	import { status_configurator } from 'src/helpers/device_data_helpers';
	import { isCameraDevice, real_id } from 'src/helpers/interface_helpers';
	import { getDeviceStatus, getDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	import { devices } from 'src/stores/lists/devices';
	import { getDeviceState, defaultStateType } from 'src/stores/device-runtime/states';
	import { bulkActionEdit, edit } from 'src/stores/ui/edit_lists';
	import { sidePanelStateCombined } from 'src/stores/ui/sidepanel-public';
	import { beforeUpdate, onDestroy } from 'svelte';
	import { format } from 'svelte-i18n';
	import DashDeviceQuickActions from './components/DashDeviceQuickActions.svelte';
	import DashCameraSingleDeviceCard from './DashCameraSingleDeviceCard.svelte';
	import MainControlStatusSlot from 'src/components/Lists/Devices/MainControlStatusSlot.svelte';
	import {
		getAdditionalText,
		getAvatar,
		formatTimerCountdown,
	} from 'src/components/Dashboards/dash-helpers';
	import type { DashCameraWidgetConfig } from 'src/components/Dashboards/dash-types';
	import { getDeviceOnOffState, getDeviceTimerState } from 'src/helpers/device_control_on_off';
	import type { DeviceTimerState } from 'src/helpers/device_control_on_off';
	import {
		getCriticalNotifications,
		getNotificationsForDevice,
	} from 'src/helpers/notifications/device_notifications_helper';
	import { hasBleDashStatusInvalidatingNotification } from 'src/helpers/dash_device_ble_status';
	import { resolveDashDeviceInfoLabels } from 'src/helpers/dash_device_info_labels';
	import { resolveDashDeviceActiveSurface } from 'src/helpers/dash_device_active_surface';
	import type { DashDeviceActiveSurfaceOverride } from 'src/helpers/dash_device_active_surface';
	import { getDashLightingPack } from 'src/helpers/dash-lighting';
	import { dashLightingPackId } from 'src/stores/features/dev/dash-lighting-pack';

	export let deviceWidget;
	export let editMode = false;
	export let showFullscreen = false;
	export let cameraConfig: DashCameraWidgetConfig | undefined;
	export let activeSurfacePreset: ActiveSurfacePresetId | undefined;
	export let surfaceGlowOverride: SurfaceGlowConfig | undefined;
	/** When true (default), lights / Cury use coloured glow from status/settings. */
	export let enableLightColor = true;
	export let lightColor: string | null | undefined;

	let statuses;
	let notAuthenticated = false;
	let isLoading = false;
	let loadingRequest = false;

	let timerState: DeviceTimerState = { hasTimer: false, timer: null, timerStartedAt: undefined };
	let tickInterval: ReturnType<typeof setInterval> | null = null;
	let cachedTimerStartedAt: number | undefined;

	function startTimerTick() {
		stopTimerTick();
		tickInterval = setInterval(() => {
			if (!device || !status || !settings) return;

			const nextTimerState = getDeviceTimerState(device, status, settings);
			timerState = nextTimerState;

			if (!nextTimerState.hasTimer || !nextTimerState.timer?.time) stopTimerTick();
		}, 50);
	}

	function stopTimerTick() {
		if (tickInterval) {
			clearInterval(tickInterval);
			tickInterval = null;
		}
	}

	beforeUpdate(() => {
		if (device && Object.keys(device).length && Object.keys(deviceState).length) {
			statuses = status_configurator(status, device, settings);
			notAuthenticated = deviceState?.type === 9;
		}
	});

	onDestroy(() => stopTimerTick());

	$: deviceStateStore = getDeviceState(device?.id);
	$: deviceState = $deviceStateStore;
	$: device = $devices[deviceWidget?.id];
	$: statusStore = getDeviceStatus(device?.id);
	$: status = $statusStore;
	$: settingsStore = getDeviceSettings(device?.id);
	$: settings = $settingsStore;
	$: notificationsToShow = getNotificationsForDevice(device?.id);
	$: criticalNotifications = getCriticalNotifications(notificationsToShow);
	$: suppressStatusLabels = hasBleDashStatusInvalidatingNotification(notificationsToShow);
	$: value = !device ? deviceWidget?.id : device?.name;
	$: avatar = getAvatar(device);
	$: isDefaultState = deviceState?.type == defaultStateType;
	$: storeTimerState =
		device && status && settings
			? getDeviceTimerState(device, status, settings)
			: { hasTimer: false, timer: null, timerStartedAt: undefined };

	$: isLoading = deviceState?.isLoading;

	$: {
		if (storeTimerState.hasTimer && !loadingRequest && !isLoading && isDefaultState) {
			if (storeTimerState.timerStartedAt !== cachedTimerStartedAt) {
				cachedTimerStartedAt = storeTimerState.timerStartedAt;
				startTimerTick();
			} else if (!tickInterval) {
				startTimerTick();
			}
		} else {
			if (tickInterval) stopTimerTick();
			if (!loadingRequest && !isLoading && isDefaultState) {
				timerState = storeTimerState;
			} else {
				timerState = { hasTimer: false, timer: null, timerStartedAt: undefined };
				cachedTimerStartedAt = undefined;
			}
		}
	}

	$: isUpdating = deviceState?.type === 3;

	$: isOffline = !device || !status?.online;
	$: isCamera = isCameraDevice(device);
	$: isSelected = deviceWidget.id === $sidePanelStateCombined?.id;
	$: onOffState =
		device && status ? getDeviceOnOffState(device, status, settings, deviceState?.type) : null;
	$: activeSurfaceOverride = ((): DashDeviceActiveSurfaceOverride | undefined => {
		if (activeSurfacePreset || surfaceGlowOverride) {
			return {
				preset: activeSurfacePreset,
				surfaceGlow: surfaceGlowOverride,
			};
		}
		return undefined;
	})();
	$: activeSurface = resolveDashDeviceActiveSurface(
		{
			device,
			status,
			settings,
			onOffState,
			offline: isOffline,
			deviceStateType: deviceState?.type,
			override: activeSurfaceOverride,
			enableLightColor,
			lightColor,
		},
		getDashLightingPack($dashLightingPackId),
	);
	$: timerCountdown =
		timerState?.hasTimer && timerState?.timer?.time
			? formatTimerCountdown(timerState.timer.time)
			: undefined;
	$: hasCriticalNotification = criticalNotifications?.length > 0 || suppressStatusLabels;
	$: infoLabelsResult =
		device && status
			? resolveDashDeviceInfoLabels({
					device,
					statuses: status_configurator(status, device, settings),
					status,
					settings,
					offline: isOffline,
					isUpdating,
					notAuthenticated,
					isDefaultState,
					hasCriticalNotification,
					hasTimer: timerState?.hasTimer,
					timerCountdown,
				})
			: {
					showInfoSlot: false,
					labels: [],
					timerInInfoSlot: false,
					rightSlotLabel: null,
					suppressVirtualMainLabel: false,
				};
	$: showInfoSlot = infoLabelsResult.showInfoSlot;
	$: infoLabels = infoLabelsResult.labels;
	$: dashRightSlotLabel = infoLabelsResult.rightSlotLabel;
	$: suppressVirtualMainLabel = infoLabelsResult.suppressVirtualMainLabel;
	$: hideSlotRight =
		!device ||
		$edit ||
		suppressStatusLabels ||
		(device?.mainActionType === 'none' && isDefaultState && !dashRightSlotLabel);
	$: additionalText = getAdditionalText(
		device,
		isUpdating,
		infoLabelsResult.timerInInfoSlot ? false : timerState?.hasTimer,
		infoLabelsResult.timerInInfoSlot ? undefined : timerState?.timer?.time,
		deviceState,
		criticalNotifications,
	);
</script>

{#if isCamera}
	<DashCameraSingleDeviceCard
		variant="button"
		{cameraConfig}
		{value}
		{additionalText}
		{editMode}
		hideSlotEditModeAction={!$edit || !$bulkActionEdit}
		{device}
		{status}
		{settings}
		offline={isOffline}
		selected={isSelected}
		on:click
		on:arrow-click
		on:fullscreen
		on:handle-drag-start
		on:handle-drag-end
	>
		<svelte:fragment slot="editModeAction">
			<MainControlStatusSlot
				useV2Components={true}
				{device}
				{status}
				{settings}
				{statuses}
				{notAuthenticated}
				on:handle-drag-start
				on:handle-drag-end
			/>
		</svelte:fragment>
	</DashCameraSingleDeviceCard>
{:else}
	<DashDeviceWidget
		aria-label={$format('aria_parameter_widget_card', { default: 'Parameter widget card' })}
		isFocusable
		{avatar}
		{value}
		{additionalText}
		offline={isOffline}
		a11yProps={{
			indicatorOnAriaLabel: $format('aria_label_indicator', { default: 'Label indicator' }),
		}}
		{hideSlotRight}
		hideSlotInfo={!showInfoSlot}
		selected={isSelected}
		active={isUpdating ? false : activeSurface.active}
		activeSurfaceMode={activeSurface.activeSurfaceMode}
		surfaceGlow={activeSurface.surfaceGlow}
		glowPulse={suppressStatusLabels
			? false
			: activeSurface.presetId === 'light-color'
				? 'subtle'
				: true}
		arrowRight
		on:click
		on:arrow-click
		on:handle-drag-start
		on:handle-drag-end
		{editMode}
	>
		<div slot="info" style="display: contents">
			{#each infoLabels as label (label.name)}
				{@const faceActive =
					activeSurface.active &&
					!isOffline &&
					!isUpdating &&
					activeSurface.activeSurfaceMode === 'opaque' &&
					isOpaqueWhiteActiveBackground(activeSurface.surfaceGlow?.backgroundColor)}
				<LabelV2
					size="m"
					value={label.value}
					icon={label.icon}
					iconColor={faceActive ? '--color-1' : label.iconColor || '--color-white'}
					iconStyle={label.iconStyle}
					textClass={faceActive ? 'text-color-base' : 'text-white'}
					noBackground={label.noBackground}
					noPadding={label.noPadding}
				/>
			{/each}
		</div>
		<DashDeviceQuickActions
			slot="right"
			useV2Components={true}
			{device}
			{status}
			{settings}
			{statuses}
			{notAuthenticated}
			{dashRightSlotLabel}
			{suppressVirtualMainLabel}
			{suppressStatusLabels}
		/>
	</DashDeviceWidget>
{/if}
